import React, { useState, useEffect } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, Network, Monitor, Users, FileCode,
  Settings, Shield, Bell, LogOut, ChevronLeft, ChevronRight,
  AlertTriangle, Wifi, Server, Building2, Wrench, Menu, X, Key, Zap, ShieldCheck, Globe, Mail, ArrowLeftRight, Package, Boxes, Smartphone } from 'lucide-react'
import { useAuthStore } from '../../store/auth'
import { useThemeStore } from '../../store/theme'
import { VelanoxLogo } from '../VelanoxLogo'
import { authApi, idleSeconds, signOut } from '../../lib/api'
import { useToast } from '../common/Toast'
import { useQuery } from '@tanstack/react-query'
import { adminApi, monitoringApi } from '../../lib/api'
import { ReauthPrompt } from '../ReauthPrompt'
import { InboxMenu } from './InboxMenu'
import type { Protection } from '../TwoFactor'
import { useT } from '../../i18n'
import { forgetPhone } from '../../lib/app/phone'
import { confirmLeave } from '../../lib/app/scans'
import { appBridge, inApp } from '../../lib/app/bridge'
import OfflineBar from './OfflineBar'
import { backdropStyle } from '../company/CompanyLogo'
import SiteSeen from '../pushfirst/SiteSeen'

interface NavItem {
  to: string
  icon: React.ReactNode
  label: string
  permission?: string
  adminOnly?: boolean
  show?: boolean
  module?: string
  badge?: number
}

export default function AppShell() {
  const company = useAuthStore(s => s.company)
  const { name, email, isCompanyAdmin, branding, hasPermission, hasModule, licenseValid, logout, activeCompanyId, refreshMe, inConsole, bound, destinations, setDestinations } = useAuthStore()
  const { theme, toggle: toggleTheme } = useThemeStore()
  const t = useT()
  const atConsole = inConsole()

  useEffect(() => {
    // a member's rights; a client person (the portal) has none here
    if (activeCompanyId && useAuthStore.getState().membershipId) refreshMe()
  }, [activeCompanyId])
  // where else this sign-in can go (Switch company): kept current, a company may have been added
  useEffect(() => {
    authApi.destinations().then(r => setDestinations(r.data.data)).catch(() => undefined)
  }, [])
  // another place to go: a company, Platform administration, or a client view (one of its own company counts when bound)
  // (Platform administration is not in the app)
  const canSwitch = destinations.companies.length + (destinations.clients?.length ?? 0) + (!bound && destinations.platformAdmin && !inApp() ? 1 : 0) > 1
  const { success: toastSuccess } = useToast()
  const navigate = useNavigate()
  // in the app: no Platform administration; Android's back button goes back here first; the status bar follows the theme
  useEffect(() => { if (atConsole && inApp()) navigate('/choose', { replace: true }) }, [atConsole])
  useEffect(() => {
    const app = appBridge()
    if (!app) return
    // one listener while the dashboard is open: it opens again after a sign-in in between
    return app.onBack(() => { if (window.history.length > 1 && window.location.pathname !== '/dashboard') { window.history.back(); return true } return false })
  }, [])
  useEffect(() => {
    const app = appBridge()
    if (!app) return
    const bg = getComputedStyle(document.body).backgroundColor.match(/\d+/g)?.map(Number) ?? [0, 0, 0]
    app.setStatusBar((bg[0] * 299 + bg[1] * 587 + bg[2] * 114) / 1000 < 128)
  }, [theme])
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  // Active alert count for badge
  const { data: alertsData } = useQuery({
    queryKey: ['alerts-count'],
    queryFn: () => monitoringApi.alerts(false, 1, 1),   // only the counts are needed
    refetchInterval: 60_000,
    enabled: !!activeCompanyId,
  })
  const levels: Record<string, number> = alertsData?.data?.meta?.levels ?? {}
  const alertCount = (levels.ERROR ?? 0) + (levels.CRITICAL ?? 0)

  // Sign-in protection: a password found in a breach list, and the inactivity limit (the API
  // enforces it; here the screen also clears itself when it is reached, polling or not)
  const { data: protection } = useQuery({
    queryKey: ['protection'], queryFn: () => authApi.protection().then(r => r.data.data as Protection), staleTime: 5 * 60_000,
  })
  const idleMinutes = protection?.idle.minutes ?? null
  useEffect(() => {
    if (idleMinutes === null) return
    const t = setInterval(() => {
      if (idleSeconds() > idleMinutes * 60) { authApi.logout().catch(() => undefined).finally(() => signOut('idle')) }
    }, 15_000)
    return () => clearInterval(t)
  }, [idleMinutes])

  const pushSettings = useQuery({ queryKey: ['platform-settings'], queryFn: () => adminApi.platformSettings(), enabled: atConsole, staleTime: 60_000 })
  const relaying = (pushSettings.data?.data?.data as { pushInUse?: { mode: string } } | undefined)?.pushInUse?.mode === 'direct'
  const navItems: NavItem[] = atConsole
    ? [
        { to: '/admin',               icon: <Building2 size={18} />,   label: t('nav.overview') },
        { to: '/admin/global-admins', icon: <ShieldCheck size={18} />, label: t('nav.admins') },
        { to: '/admin/companies', icon: <Network size={18} />, label: t('nav.companies') },
        { to: '/admin/models', icon: <Boxes size={18} />, label: t('nav.models') },
        // only an installation that relays pushes for others has a list of them
        ...(relaying ? [{ to: '/admin/push-relay', icon: <Smartphone size={18} />, label: t('nav.pushRelay') }] : []),
        { to: '/admin/settings', icon: <Settings size={18} />, label: t('nav.settings') },
        { to: '/admin/vpn',  icon: <Wifi size={18} />,   label: t('nav.vpn') },
        { to: '/admin/edge', icon: <Globe size={18} />,  label: t('nav.edge') },
        { to: '/admin/licenses',         icon: <Shield size={18} />, label: t('nav.licensePlans') },
        { to: '/admin/license-overview', icon: <Key size={18} />,    label: t('nav.licenseOverview') },
        { to: '/profile', icon: <Users size={18} />, label: t('nav.myProfile') },
      ]
    : [
        { to: '/dashboard', icon: <LayoutDashboard size={18} />, label: t('nav.dashboard') },
        { to: '/monitoring', icon: <Monitor size={18} />, label: t('nav.monitoring'), permission: 'MONITORING_VIEW' },
        { to: '/monitoring/alerts', icon: <AlertTriangle size={18} />, label: t('nav.alerts'), badge: alertCount || undefined, permission: 'MONITORING_VIEW' },
        { to: '/sites', icon: <Server size={18} />, label: t('nav.sites'), permission: 'SITE_VIEW' },
        { to: '/templates', icon: <FileCode size={18} />, label: t('nav.templates'), permission: 'TEMPLATE_VIEW', module: 'templates' },
        { to: '/rental', icon: <Package size={18} />, label: t('nav.rental'), permission: 'RENTAL_VIEW', module: 'rental' },
        { to: '/users', icon: <Users size={18} />, label: t('nav.users'), permission: 'USER_VIEW' },
        { to: '/users/roles', icon: <Shield size={18} />, label: t('nav.roles'), permission: 'USER_VIEW' },
        { to: '/settings/branding', icon: <Wrench size={18} />, label: t('nav.branding'), permission: 'COMPANY_BRANDING', module: 'branding' },
        { to: '/settings/messages', icon: <Mail size={18} />, label: t('nav.messages'), permission: 'COMPANY_BRANDING', module: 'branding' },
        { to: '/settings/company', icon: <Settings size={18} />, label: t('nav.settings'), permission: 'COMPANY_SETTINGS' },
        { to: '/settings/vpn', icon: <Wifi size={18} />, label: t('nav.vpn'), permission: 'VPN_VIEW' },
        { to: '/settings/api-keys', icon: <Key size={18} />, label: t('nav.apiKeys'), permission: 'APIKEY_VIEW', module: 'apiKeys' },
        { to: '/settings/webhooks', icon: <Zap size={18} />, label: t('nav.webhooks'), permission: 'COMPANY_SETTINGS', module: 'webhooks' },
        { to: '/notifications', icon: <Bell size={18} />, label: t('nav.notifications') },
        { to: '/profile', icon: <Users size={18} />, label: t('nav.myProfile') },
      ]

  const visibleItems = navItems.filter((item) =>
    item.show !== false &&
    (!item.permission || hasPermission(item.permission)) &&
    (!item.module || hasModule(item.module))
  )

  const handleProfileClick = () => navigate('/profile')

  const handleLogout = async () => {
    // scans not sent yet: asked first (signing out drops them)
    if (!await confirmLeave(t)) return
    // in the app: this phone no longer stays signed in
    await forgetPhone()
    try { await authApi.logout() } catch {}
    logout()
    navigate('/login')
  }

  const appName = branding.appName || 'Velanox'

  return (
    <div className="app-shell">
      <OfflineBar />
      <SiteSeen />
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="mobile-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        {/* Logo */}
        <div className="sidebar-logo">
          {branding.logoUrl
            ? <img src={branding.logoUrl} alt={appName} style={{ maxHeight: 28, maxWidth: '100%', objectFit: 'contain' }} />
            : <VelanoxLogo size={22} showWordmark={true} />}
        </div>

        {/* Nav */}
        <nav className="sidebar-nav">
          {visibleItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={navItems.some(o => o.to.startsWith(item.to + '/'))}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              title={collapsed ? item.label : undefined}
            >
              <span className="nav-icon">{item.icon}</span>
              {!collapsed && <span className="nav-label">{item.label}</span>}
              {!collapsed && item.badge ? (
                <span className="nav-badge">{item.badge}</span>
              ) : null}
              {collapsed && item.badge ? (
                <span className="nav-badge-dot" />
              ) : null}
            </NavLink>
          ))}
        </nav>

        {/* User & collapse */}
        <div className="sidebar-footer">
          <div className="user-info" title={collapsed ? name ?? undefined : undefined}>
            <div className="user-avatar">
              {name?.slice(0, 1).toUpperCase()}
            </div>
            {!collapsed && (
              <div className="user-details">
                <span className="user-name">{name}</span>
                <span className="user-role">{atConsole ? t('nav.roleGlobalAdmin') : isCompanyAdmin ? t('nav.roleAdmin') : t('nav.roleUser')}</span>
              </div>
            )}
          </div>
          {canSwitch && (
            <button className="logout-btn" onClick={() => navigate('/choose')} title={t('nav.switchCompany')} aria-label={t('nav.switchCompany')}>
              <ArrowLeftRight size={16} />
            </button>
          )}
          <button className="logout-btn" onClick={handleLogout} title={t('nav.logout')} aria-label={t('nav.logout')}>
            <LogOut size={16} />
          </button>
        </div>

        {/* Collapse toggle */}
        <button
          className="collapse-toggle"
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? t('nav.expand') : t('nav.collapse')}
          aria-label={collapsed ? t('nav.expandMenu') : t('nav.collapseMenu')}
          aria-expanded={!collapsed}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </aside>

      {/* Main content */}
      <div className={`main-area ${collapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Topbar */}
        <header className="topbar">
          <button aria-label={mobileOpen ? t('nav.closeMenu') : t('nav.openMenu')} aria-expanded={mobileOpen} className="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          {/* the company: its banner logo, or else its name, in the middle */}
          {company && !atConsole && (
            <div className="topbar-company">{company.bannerUrl
              ? <span className={`pic-backdrop${backdropStyle(company.bannerUrl) ? ' has-backdrop' : ''}`} style={backdropStyle(company.bannerUrl)}><img src={company.bannerUrl} alt={company.name} /></span>
              : company.name}</div>
          )}
          <div className="topbar-spacer" />
          <div className="topbar-actions">
            <InboxMenu />
            {alertCount > 0 && (
              <button className="topbar-alert-btn" onClick={() => navigate('/monitoring/alerts')}>
                <Bell size={18} />
                <span className="topbar-badge">{alertCount}</span>
              </button>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="page-content">
          {!licenseValid && (
            <div className="alert-bar alert-bar-error" role="alert" style={{ marginBottom: 16 }}>
              {t('nav.licenseExpired')}
            </div>
          )}
          {protection?.passwordLeaked && (
            <div className="alert-bar alert-bar-warning" role="alert" style={{ marginBottom: 16 }}>
              {t('nav.passwordLeaked')}{' '}
              {t('nav.passwordLeakedChange')} <NavLink to="/profile">{t('nav.myProfile')}</NavLink>.
            </div>
          )}
          <Outlet />
        </main>
        <ReauthPrompt sso={!!protection?.sso} />
      </div>
    </div>
  )
}
